import type { ComponentType } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { filterCode, isFullCode } from '../codeInput'
import type { LeaveReason } from '../../net/leaveReason'
import { errorText } from '../text'
import styles from './overlays.module.css'
import { Icon } from './icons'

/**
 * "Play with friends" popover (PHASE3 T3.1): Private room, and the Join-code form moved from
 * the old menu (same `codeInput.ts` rules). A refused code shows its error here with the cursor
 * back in the box; Phase 2's party panel (D-053) mounts at the bottom when it exists.
 */
export function PlayWithFriends({
  initialCode,
  error,
  onPrivate,
  onJoin,
  onClose,
  PartyPanel,
}: {
  initialCode: string
  error: LeaveReason | null
  onPrivate: () => void
  onJoin: (code: string) => void
  onClose: () => void
  PartyPanel: ComponentType | null
}) {
  const [code, setCode] = useState(initialCode)
  const codeField = useRef<HTMLInputElement>(null)
  const first = useRef<HTMLButtonElement>(null)
  const codeError = error === 'not-found'

  useEffect(() => {
    if (codeError) codeField.current?.focus()
    else first.current?.focus()
  }, [codeError])

  const onCode = (e: Event): void => {
    const input = e.currentTarget as HTMLInputElement
    // No `maxLength`: it would cut a pasted "ab-9z" before the filter drops the dash.
    const next = filterCode(input.value)
    input.value = next
    setCode(next)
  }

  const submit = (e: Event): void => {
    e.preventDefault()
    if (isFullCode(code)) onJoin(code)
  }

  return (
    <div class={`${styles.card} ${styles.popover}`} role="dialog" aria-label="Play with friends">
      <div class={styles.popoverHead}>
        <strong>Play with friends</strong>
        <button type="button" class={styles.closeButton} aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <button ref={first} type="button" class={styles.wideButton} onClick={onPrivate}>
        Private room
        <small>Get a code to share</small>
      </button>
      <form class={styles.codeRow} onSubmit={submit}>
        <input
          ref={codeField}
          class={styles.codeBox}
          value={code}
          onInput={onCode}
          placeholder="CODE"
          aria-label="Room code"
          aria-invalid={codeError}
          autoComplete="off"
          autoCapitalize="characters"
          spellcheck={false}
          enterKeyHint="go"
        />
        <button type="submit" class={styles.joinButton} disabled={!isFullCode(code)}>
          Join
        </button>
      </form>
      {codeError && (
        <p class={styles.popoverError} role="alert">
          {errorText('not-found')}
        </p>
      )}
      {PartyPanel ? <PartyPanel /> : null}
    </div>
  )
}
